// src/services/StorageService.js
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { auth } from '../firebase';

// Initialize Firebase Storage
const storage = getStorage();

// Allowed file types
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/mpeg', 'video/quicktime', 'video/x-msvideo'];

/**
 * Upload a single media file (image or video) to Firebase Storage
 * @param {File} file - The file to upload
 * @param {string} listingId - Optional listing ID to use in the path
 * @returns {Promise<{url: string, type: string}>} - The download URL and file type
 */
export const uploadMedia = async (file, listingId = null) => {
  try {
    // Check file type
    const isImage = ALLOWED_IMAGE_TYPES.includes(file.type);
    const isVideo = ALLOWED_VIDEO_TYPES.includes(file.type);
    
    if (!isImage && !isVideo) {
      throw new Error('Only JPEG, JPG, PNG, GIF images and MP4, MPEG, MOV, AVI videos are allowed');
    }
    
    // Check file size
    const maxSize = isVideo ? 10 * 1024 * 1024 : 2 * 1024 * 1024; // 10MB for videos, 2MB for images
    if (file.size > maxSize) {
      const sizeMB = isVideo ? 10 : 2;
      throw new Error(`File size must be less than ${sizeMB}MB`);
    }
    
    // Create a unique filename
    const timestamp = new Date().getTime();
    const userId = auth.currentUser?.uid || 'unknown';
    const extension = file.name.split('.').pop();
    const fileName = listingId 
      ? `listings/${listingId}/${timestamp}_${file.name}` 
      : `listings/${userId}/${timestamp}_${file.name}`;
    
    // Create storage reference
    const storageRef = ref(storage, fileName);
    
    // Upload file
    const snapshot = await uploadBytes(storageRef, file);
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return {
      url: downloadURL,
      type: isVideo ? 'video' : 'image'
    };
  } catch (error) {
    console.error('Error uploading media:', error);
    throw error;
  }
};

/**
 * Upload multiple media files
 * @param {File[]} files - Array of files to upload
 * @param {string} listingId - Optional listing ID
 * @returns {Promise<Array<{url: string, type: string}>>} - Array of upload results
 */
export const uploadMultipleMedia = async (files, listingId = null) => {
  try {
    const uploadPromises = files.map(file => uploadMedia(file, listingId));
    const results = await Promise.all(uploadPromises);
    return results;
  } catch (error) {
    console.error('Error uploading multiple media files:', error);
    throw error;
  }
};

/**
 * Upload an image file to Firebase Storage (legacy support)
 * @param {File} file - The file to upload
 * @param {string} listingId - Optional listing ID to use in the path
 * @returns {Promise<string>} - The download URL of the uploaded file
 */
export const uploadImage = async (file, listingId = null) => {
  const result = await uploadMedia(file, listingId);
  return result.url;
};

/**
 * Delete a media file from Firebase Storage
 * @param {string} mediaUrl - The URL of the media to delete
 * @returns {Promise<boolean>} - True if successful
 */
export const deleteMedia = async (mediaUrl) => {
  try {
    // Extract the path from the URL
    const urlObj = new URL(mediaUrl);
    const path = urlObj.pathname;
    
    // Remove the "/o/" prefix and decode the path
    const storagePath = decodeURIComponent(path.split('/o/')[1].split('?')[0]);
    
    // Create a reference to the file
    const mediaRef = ref(storage, storagePath);
    
    // Delete the file
    await deleteObject(mediaRef);
    
    return true;
  } catch (error) {
    console.error('Error deleting media:', error);
    // If file doesn't exist, consider it successfully deleted
    if (error.code === 'storage/object-not-found') {
      return true;
    }
    throw error;
  }
};

/**
 * Delete multiple media files
 * @param {string[]} mediaUrls - Array of URLs to delete
 * @returns {Promise<boolean[]>} - Array of deletion results
 */
export const deleteMultipleMedia = async (mediaUrls) => {
  try {
    const deletePromises = mediaUrls.map(url => deleteMedia(url));
    const results = await Promise.all(deletePromises);
    return results;
  } catch (error) {
    console.error('Error deleting multiple media files:', error);
    throw error;
  }
};

// Legacy support
export const deleteImage = deleteMedia;